import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import {
  customDeliveryInstructionValidationMessage,
  deliveryInstructionsFields,
} from 'DeliveryInstructions/DeliveryInstructionsConfig'
import moment from 'moment'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import type {
  LoggedOutBoxSummaryController,
  LoggedOutBoxSummaryPortionSize,
} from '@features/box-summary'
import type { RecipeUUID } from '@library/menu-service'
import { queueTrackingEvent } from '@library/tracking-integration'

import { actionTypes } from 'actions/actionTypes'
import {
  basketIsShowDetailsViewFirstChange,
  basketNumPortionChange,
  basketPostcodeClear,
  basketRestorePreviousDate,
  basketSlotClear,
} from 'actions/basket'
import {
  trackBoxSizeSelectionChange,
  trackDeliveryInstructionSelection,
  trackDeliveryOptionsShown,
  trackRecipeChangeWarning,
} from 'actions/boxSummary'
import { setTempDeliveryOptions } from 'actions/deliveries'
import {
  saveDeliveryDetailsInstruction,
  saveDeliveryDetailsInstructionCustom,
} from 'actions/deliveryDetails'
import tempActions from 'actions/temp'
import { boxSummary as boxSummarySection, safePlaceDropDownSelected } from 'actions/trackingKeys'
import { portionSizesConfig } from 'config/portionSizes'
import { useAvailableBoxSizes } from 'hooks/basket/useAvailableBoxSizes'
import { useBasketRecipes } from 'hooks/basket/useBasketRecipes'
import { useMaxRecipesForPortion } from 'hooks/menu-boxes/useMaxRecipesForPortion'
import { useMinRecipesForPortion } from 'hooks/menu-boxes/useMinRecipesForPortion'
import { checkoutBasket } from 'routes/Menu/actions/menuCheckoutClick'
import { menuRecipeDetailVisibilityChange } from 'routes/Menu/actions/menuRecipeDetails'
import { notPrimaryMenuWeekWarningCopy } from 'routes/Menu/components/BoxSummary/DeliverySlot/NotPrimaryMenuWeekWarning/NotPrimaryMenuWeekWarning'
import { isNotPrimaryMenuWeek } from 'routes/Menu/components/MenuDateRange/utils'
import { useBasket } from 'routes/Menu/domains/basket'
import { usePricing } from 'routes/Menu/domains/pricing'
import { useGetCurrentBoxSummaryView } from 'routes/Menu/hooks/useGetCurrentBoxSummaryView'
import { getDisabledSlotDates } from 'routes/Menu/selectors/boxSummary'
import { getHasProspectNotSeenWizard } from 'routes/Menu/selectors/menu'
import { trackDisplayEarliestDeliveryDate } from 'routes/Signup/Steps/Delivery/utils/tracking'
import { getBasketDate, getBasketRecipesCount, getBasketSlotId } from 'selectors/basket'
import {
  getDeliveryDetailsInstructions,
  getDeliveryDetailsInstructionsCustom,
  getIsValidDeliveryDetailsInstructionsCustom,
} from 'selectors/deliveryDetails'
import { getUserOrders } from 'selectors/user'
import { getLandingDay, getSlotTimes } from 'utils/deliveries'
import {
  addDisabledSlotIds,
  getDeliveryDaysAndSlots,
  getTempDeliveryOptions,
} from 'utils/deliverySlotHelper'

import { legacyViewToTreatmentStep } from './legacyViewToTreatmentStep'
import type { BoxSummaryTreatmentProps } from './props'

const DEFAULT_PORTION_SIZE: LoggedOutBoxSummaryPortionSize = 2
const PORTION_SIZES: LoggedOutBoxSummaryPortionSize[] = [1, 2, 3, 4, 5]
const POSTCODE_CONSTRAINTS = {
  minLength: 5,
  maxLength: 8,
}
const CUSTOM_DELIVERY_INSTRUCTION_CONSTRAINTS = {
  maxLength: 32,
}
const deliveryInstructionOptions = deliveryInstructionsFields.leaveBoxOptions.map((option) => ({
  value: option,
  label: option,
  requiresCustomValue: deliveryInstructionsFields.leaveBoxOptionsWithMessage.includes(option),
}))

// Shape of an entry in `getDeliveryDaysAndSlots(...).deliveryDays`, which is
// loosely typed upstream (built from Immutable maps). Typing the fields we read
// keeps the view-model construction honest about the contract it depends on.
type DeliveryDayOption = {
  date: string
  value: string
  label?: string
  disabled?: boolean
  icon?: string | null
  orderId?: string | null
  ordered?: boolean
}

const isPortionSize = (portionSize: number): portionSize is LoggedOutBoxSummaryPortionSize =>
  PORTION_SIZES.includes(portionSize as LoggedOutBoxSummaryPortionSize)

const getPortionSizeOrDefault = (portionSize: number): LoggedOutBoxSummaryPortionSize =>
  isPortionSize(portionSize) ? portionSize : DEFAULT_PORTION_SIZE

const isDeliveryInstructionOption = (instruction: string) =>
  deliveryInstructionsFields.leaveBoxOptions.includes(instruction)

const getPostcodeValidationMessage = (
  deliveryDaysError: string | boolean | Record<string, unknown> | null | undefined,
) => {
  if (!deliveryDaysError) {
    return null
  }

  if (deliveryDaysError === 'do-not-deliver') {
    return "Sorry, it looks like we don't currently deliver to your area."
  }

  return 'Please enter a valid postcode'
}

const trackSafePlaceDropdownSelection = (selection: string) => ({
  type: actionTypes.TRACKING,
  trackingData: {
    actionType: safePlaceDropDownSelected,
    selection,
  },
})

export const useLoggedOutBoxSummaryController = ({
  showDetails,
  boxDetailsVisibilityChange,
  basketIsPortionSizeViewVisibleChange,
  basketRestorePreviousValues,
  boxSummaryNext,
  restoreFocusAfterClose,
  numPortions,
}: BoxSummaryTreatmentProps): LoggedOutBoxSummaryController => {
  const dispatch = useDispatch<any>()
  const tempPostcode = useSelector((state: any) => state.temp.get('postcode', '')) ?? ''
  const deliveryDaysError = useSelector((state: any) =>
    state.error.get(actionTypes.BOXSUMMARY_DELIVERY_DAYS_RECEIVE),
  )
  const postcodePending = Boolean(useSelector((state: any) => state.basket.get('postcodePending')))
  const prevPostcode = useSelector((state: any) => state.basket.get('prevPostcode')) ?? ''
  const { removeRecipeByUuid } = useBasket()
  const { basketRecipes } = useBasketRecipes()
  const { availableBoxSizes, isLoading: arePortionSizesLoading } = useAvailableBoxSizes()
  const [selectedPortionSize, setSelectedPortionSize] = useState<LoggedOutBoxSummaryPortionSize>(
    getPortionSizeOrDefault(numPortions),
  )
  const currentView = useGetCurrentBoxSummaryView()
  const step = legacyViewToTreatmentStep(currentView)
  const availablePortionSizes = useMemo(
    () => availableBoxSizes?.filter(isPortionSize) ?? [],
    [availableBoxSizes],
  )
  const nullRecipeIds = useMemo(
    () => basketRecipes.filter(({ recipe }) => recipe === null).map(({ id }) => id),
    [basketRecipes],
  )
  // Mirror the control, which uses `getBasketRecipesCount` (total basket recipes)
  // for both the portion-size over-limit warning and the not-primary-menu-week
  // warning. The `numRecipes` prop is `getBasketUsageCount`, which only counts
  // recipes still available on the current menu and would wrongly suppress both
  // warnings when the basket contains unavailable recipes.
  const totalRecipesInBasket = useSelector(getBasketRecipesCount)
  const maxRecipesForPortionSize = useMaxRecipesForPortion(selectedPortionSize)
  const minRecipesForPortionSize = useMinRecipesForPortion(selectedPortionSize)
  const { pricing, isPending: isPricingPending } = usePricing()
  const numRecipesToRemove = totalRecipesInBasket - maxRecipesForPortionSize
  const hasTooManyRecipes = totalRecipesInBasket > maxRecipesForPortionSize
  const trimmedPostcode = tempPostcode.trim()
  const canConfirmPortionSize =
    !arePortionSizesLoading && availablePortionSizes.includes(selectedPortionSize)

  const basketPostcode = useSelector((state: any) => state.basket.get('postcode')) ?? ''
  const prevSlotId = useSelector((state: any) => state.basket.get('prevSlotId')) ?? ''
  const boxSummaryDeliveryDays = useSelector((state: any) => state.boxSummaryDeliveryDays)
  // `getTempDeliveryOptions` rebuilds its result (and re-runs `addDisabledSlotIds`
  // / `getLandingDay`) on every call, so read it once and shallow-compare the two
  // primitives we need rather than subscribing twice for one slice.
  const { tempDate, tempSlotId } = useSelector((state: any) => {
    const tempDeliveryOptions = getTempDeliveryOptions(state)

    return {
      tempDate: tempDeliveryOptions.tempDate ?? '',
      tempSlotId: tempDeliveryOptions.tempSlotId ?? '',
    }
  }, shallowEqual)
  const earliestDeliveryDate = useSelector(
    (state: any) => getLandingDay(state, { cantLandOnOrderDate: true, useBasketDate: false }).date,
  )
  const slotId = useSelector(getBasketSlotId)
  const disabledSlots = useSelector(getDisabledSlotDates)
  const userOrders = useSelector(getUserOrders)
  const basketDate = useSelector(getBasketDate)
  const hasProspectNotSeenWizard = useSelector(getHasProspectNotSeenWizard)
  const deliveryInstruction = useSelector(getDeliveryDetailsInstructions) ?? ''
  const deliveryInstructionCustom = useSelector(getDeliveryDetailsInstructionsCustom) ?? ''
  const isValidDeliveryInstructionCustom = useSelector(getIsValidDeliveryDetailsInstructionsCustom)
  const menuPending = useSelector((state: any) => Boolean(state.menuReceiveMenuPending))
  const menuFetchDataPending = useSelector((state: any) =>
    Boolean(state.pending.get(actionTypes.MENU_FETCH_DATA, false)),
  )
  // While the menu is still loading, `useBasketRecipes` can transiently report
  // basket recipes as null or `isAvailable: false` before the fetch settles.
  // Gate every derived "unavailable" signal on this — mirrors the control's
  // `!menuFetchPending` guard on `UnavailableMessage` and prevents a red
  // "no longer available" flash on open.
  const menuIsLoading = menuPending || menuFetchDataPending
  // Matches the control's box-summary definition of "unavailable": recipes
  // that loaded but are flagged `isAvailable: false`. Null recipes (UUIDs
  // absent from recipesV3) are excluded to match RecipeList, which filters
  // them out before rendering, so the warning count matches what the user
  // actually sees.
  const hasUnavailableRecipes =
    !menuIsLoading &&
    basketRecipes.some(
      ({ recipe, quantity }) => quantity > 0 && recipe !== null && !recipe.isAvailable,
    )

  const { deliveryDays, chosen, hasActiveSlotsForSelectedDate } = useMemo(() => {
    const deliveryDaysProps = boxSummaryDeliveryDays
      ? addDisabledSlotIds(boxSummaryDeliveryDays)
      : boxSummaryDeliveryDays

    return getDeliveryDaysAndSlots(tempDate, {
      disabledSlots,
      isAuthenticated: false,
      tempDate,
      userOrders,
      tempSlotId,
      deliveryDaysProps,
    })
  }, [boxSummaryDeliveryDays, disabledSlots, tempDate, tempSlotId, userOrders])

  const showMenuWeekWarning =
    Boolean(totalRecipesInBasket) &&
    isNotPrimaryMenuWeek(basketDate, tempDate) &&
    hasProspectNotSeenWizard

  // Fire as a post-render effect, not during render: dispatching from the render
  // body loops to "Maximum update depth exceeded". Re-fires when the date changes.
  useEffect(() => {
    if (showMenuWeekWarning) {
      dispatch(trackRecipeChangeWarning(tempDate))
    }
  }, [showMenuWeekWarning, tempDate, dispatch])

  useEffect(() => {
    setSelectedPortionSize(getPortionSizeOrDefault(numPortions))
  }, [numPortions])

  const hasTrackedEarliestDeliveryDate = useRef(false)
  useEffect(() => {
    if (!hasTrackedEarliestDeliveryDate.current && earliestDeliveryDate) {
      trackDisplayEarliestDeliveryDate(earliestDeliveryDate, 'signup')
      hasTrackedEarliestDeliveryDate.current = true
    }
  }, [earliestDeliveryDate])

  const onClose = useCallback(() => {
    basketIsPortionSizeViewVisibleChange(false)
    boxDetailsVisibilityChange(false)
    basketRestorePreviousValues()

    setTimeout(() => {
      restoreFocusAfterClose()
    }, 0)
  }, [
    basketIsPortionSizeViewVisibleChange,
    basketRestorePreviousValues,
    boxDetailsVisibilityChange,
    restoreFocusAfterClose,
  ])

  const actions = useMemo(
    () => ({
      onClose,
      onPostcodeChange: (postcode: string) => {
        dispatch(tempActions.temp('postcode', postcode))
      },
      onPostcodeContinue: boxSummaryNext,
      onDeliveryPostcodeEdit: () => {
        dispatch(basketPostcodeClear())
        dispatch(trackDeliveryOptionsShown())
      },
      onDeliveryDateSelect: (date: string) => {
        // `setTempDeliveryOptions` itself auto-selects the date's first
        // unblocked slot (the implicit logged-out slot), matching the control.
        dispatch(setTempDeliveryOptions(date))
        // Mirror control's DatePicker `enrichedHandleDateChange`, which queues
        // `choose_delivery_date` after applying the date change.
        queueTrackingEvent<{ action: 'choose_delivery_date' }>({
          action: 'choose_delivery_date',
          properties: {
            delivery_date: date,
          },
        })
      },
      onDeliveryInstructionChange: (instruction: string) => {
        if (!isDeliveryInstructionOption(instruction)) {
          return
        }

        dispatch(saveDeliveryDetailsInstruction(instruction))
        dispatch(trackSafePlaceDropdownSelection(instruction))

        if (!deliveryInstructionsFields.leaveBoxOptionsWithMessage.includes(instruction)) {
          dispatch(saveDeliveryDetailsInstructionCustom(''))
        }
      },
      onDeliveryInstructionCustomChange: (instruction: string) => {
        dispatch(saveDeliveryDetailsInstructionCustom(instruction))
      },
      onDeliveryContinue: () => {
        dispatch(trackDeliveryInstructionSelection())
        boxSummaryNext()
      },
      onPortionSizeSelect: (portionSize: LoggedOutBoxSummaryPortionSize) => {
        if (!availablePortionSizes.includes(portionSize)) {
          return
        }

        setSelectedPortionSize(portionSize)
        dispatch(trackBoxSizeSelectionChange(portionSize, 'box_summary'))
      },
      onPortionSizeConfirm: () => {
        if (!canConfirmPortionSize) {
          return
        }

        nullRecipeIds.forEach((id) => {
          removeRecipeByUuid(id)
        })
        dispatch(basketNumPortionChange(selectedPortionSize, 'box_summary'))
        basketIsPortionSizeViewVisibleChange(false)
      },
      onSummaryEditPostcode: () => {
        dispatch(basketIsShowDetailsViewFirstChange(false))
        dispatch(basketPostcodeClear())
        dispatch(trackDeliveryOptionsShown())
      },
      onSummaryEditDelivery: () => {
        dispatch(basketIsShowDetailsViewFirstChange(false))
        dispatch(basketSlotClear())
      },
      onSummaryEditPortionSize: () => {
        dispatch(basketIsShowDetailsViewFirstChange(false))
        basketIsPortionSizeViewVisibleChange(true)
      },
      onSummaryRecipeRemove: (recipeId: string) => {
        removeRecipeByUuid(recipeId as RecipeUUID, 'boxSummaryMinus')
      },
      onSummaryRecipeClick: (recipeId: string) => {
        dispatch(menuRecipeDetailVisibilityChange(recipeId))
      },
      onSummaryRestoreDeliveryDate: () => {
        dispatch(basketRestorePreviousDate())
      },
      onCheckout: () => {
        // Only proceed to checkout when the basket is exactly full (no
        // too-many-recipes overflow) AND all recipes are available. Any other
        // state closes the panel so the user can continue browsing — matching
        // the control's behaviour. The extra `!hasTooManyRecipes` guard
        // mirrors the CTA's isDisabled predicate so onCheckout and the CTA
        // stay in sync if either derivation changes later.
        if (
          totalRecipesInBasket >= maxRecipesForPortionSize &&
          !hasTooManyRecipes &&
          !hasUnavailableRecipes
        ) {
          // A delivery instruction is required before checkout. A user can
          // arrive at the summary step without one if they land directly on
          // the menu page with a basket slot already set (bypassing the wizard
          // delivery step). Navigate back to the delivery step so they can
          // provide one.
          if (!deliveryInstruction || !isValidDeliveryInstructionCustom) {
            dispatch(basketIsShowDetailsViewFirstChange(false))
            dispatch(basketSlotClear())

            return
          }

          // No `variant` field: Datadog RUM already tags this action with the active
          // Optimizely decision via the global `addFeatureFlagEvaluation` listener.
          datadogRum?.addAction('basket_checkout_button_clicked', { slotId })
          dispatch(
            checkoutBasket({
              section: boxSummarySection,
              view: 'logged_out_box_summary',
              pricing,
            }),
          )

          return
        }

        onClose()
      },
    }),
    [
      basketIsPortionSizeViewVisibleChange,
      availablePortionSizes,
      boxSummaryNext,
      canConfirmPortionSize,
      deliveryInstruction,
      dispatch,
      hasTooManyRecipes,
      hasUnavailableRecipes,
      isValidDeliveryInstructionCustom,
      nullRecipeIds,
      onClose,
      removeRecipeByUuid,
      selectedPortionSize,
      slotId,
      totalRecipesInBasket,
      maxRecipesForPortionSize,
      pricing,
    ],
  )

  const summaryView = useMemo(() => {
    const recipes = basketRecipes
      .filter(({ recipe, quantity }) => recipe !== null && quantity > 0)
      .map(({ id, recipe, quantity }) => ({
        id,
        name: recipe!.name,
        imageUrl: recipe!.images?.[0]?.crops?.[0]?.url ?? '',
        servings: quantity * selectedPortionSize,
        isUnavailable: !recipe!.isAvailable,
      }))

    const deliveryLabel =
      tempDate && tempSlotId
        ? `${moment(tempDate).format('ddd Do MMM')}, ${getSlotTimes({
            date: tempDate,
            deliveryDays: boxSummaryDeliveryDays,
            slotId: tempSlotId,
          }).trim()}`
        : null

    const emptyRecipesCopy =
      totalRecipesInBasket === 0
        ? `Add up to ${maxRecipesForPortionSize} recipes to create your Gousto order. The more you add, the lower the price per portion.`
        : null

    const infoBanner = (() => {
      // Suppress progress messaging while the higher-priority warnings
      // (unavailable recipes, too many recipes) are the actionable state.
      if (hasUnavailableRecipes || hasTooManyRecipes) return null
      if (totalRecipesInBasket <= 0) {
        return {
          message: `Add ${maxRecipesForPortionSize} recipes for the best price per portion`,
          variant: 'info' as const,
        }
      } else if (totalRecipesInBasket < minRecipesForPortionSize) {
        return { message: 'Add more recipes to complete your box', variant: 'info' as const }
      } else if (totalRecipesInBasket >= maxRecipesForPortionSize) {
        return {
          message: "Nice! You've got the best price per portion",
          variant: 'success' as const,
        }
      } else {
        const remaining = maxRecipesForPortionSize - totalRecipesInBasket

        return {
          message: `Add ${remaining} more recipe${
            remaining > 1 ? 's' : ''
          } for the best price per portion`,
          variant: 'info' as const,
        }
      }
    })()

    // Mirrors the control's TooManyRecipesWarning ("You've chosen too many
    // recipes" / "Remove N recipe(s) to check out"). Rendered as a red banner
    // by SummaryPanel when the basket exceeds the selected portion size.
    const tooManyRecipesWarning =
      !menuIsLoading && hasTooManyRecipes
        ? {
            title: "You've chosen too many recipes",
            body: `Remove ${numRecipesToRemove} recipe${
              numRecipesToRemove === 1 ? '' : 's'
            } to check out`,
          }
        : null

    const discountPercent = pricing?.percentageOff ? parseFloat(pricing.percentageOff) : 0
    const discountAmount = pricing?.recipeDiscount ? parseFloat(pricing.recipeDiscount) : 0
    const priceBreakdown = {
      recipeCount: totalRecipesInBasket,
      recipeTotal: pricing?.recipeTotal ? `£${pricing.recipeTotal}` : null,
      // Zero-valued discounts should render nothing — match the control's
      // Receipt which suppresses the row when `recipeDiscountAmount === 0`.
      // Round the percentage up (control uses `Math.ceil`) so decimals read
      // as e.g. "51% discount" instead of "50.4% discount".
      discountLabel: discountPercent > 0 ? `${Math.ceil(discountPercent)}% discount` : null,
      discountTotal: discountAmount > 0 ? `-£${pricing!.recipeDiscount}` : null,
      surchargeTotal: (() => {
        if (!pricing?.surchargeTotal) return null
        const value = parseFloat(pricing.surchargeTotal)
        if (value === 0) return null
        const abs = pricing.surchargeTotal.replace(/^-/, '')

        return value > 0 ? `+£${abs}` : `-£${abs}`
      })(),
      deliveryTotal: pricing?.deliveryTotal ? `£${pricing.deliveryTotal}` : null,
      total: pricing?.total ? `£${pricing.total}` : null,
    }

    // Count only visible unavailable recipes. Null recipes are filtered out of
    // the rendered list (matching the control's RecipeList), so counting them
    // here would show "The following 2 recipes are no longer available" while
    // only 1 greyed row is visible.
    const unavailableCount = recipes.filter((r) => r.isUnavailable).length
    const unavailableRecipesWarning =
      unavailableCount > 0
        ? `The following ${
            unavailableCount > 1 ? 'recipes are' : 'recipe is'
          } no longer available. Please choose ${
            unavailableCount > 1 ? 'different recipes' : 'another recipe'
          }.`
        : null

    return {
      title: 'Box summary',
      portionSizeLabel: portionSizesConfig[selectedPortionSize].heading,
      deliveryLabel,
      recipes,
      emptyRecipesCopy,
      unavailableRecipesWarning,
      // Only surface the "undo your date change" affordance when a previous
      // slot exists to restore to (matches the control's implicit behaviour —
      // `basketRestorePreviousDate` reads `prevSlotId` / `prevDate` and is a
      // no-op otherwise).
      canRestoreDeliveryDate: Boolean(prevSlotId) && Boolean(unavailableRecipesWarning),
      tooManyRecipesWarning,
      infoBanner,
      priceBreakdown,
      checkoutCta: (() => {
        const label = (() => {
          // When unavailable recipes are present the user must swap them out
          // before checking out — always show "Choose recipes" so the CTA
          // closes the panel and lets them browse, matching the control.
          if (hasUnavailableRecipes) return 'Choose recipes'
          if (totalRecipesInBasket >= maxRecipesForPortionSize) return 'Checkout'
          if (totalRecipesInBasket >= minRecipesForPortionSize) return 'Choose more recipes'

          return 'Choose recipes'
        })()

        return {
          label,
          isDisabled:
            totalRecipesInBasket >= minRecipesForPortionSize && (!tempSlotId || hasTooManyRecipes),
          isPending: menuPending || menuFetchDataPending || isPricingPending,
        }
      })(),
    }
  }, [
    basketRecipes,
    selectedPortionSize,
    tempDate,
    tempSlotId,
    boxSummaryDeliveryDays,
    totalRecipesInBasket,
    maxRecipesForPortionSize,
    minRecipesForPortionSize,
    menuIsLoading,
    hasTooManyRecipes,
    hasUnavailableRecipes,
    numRecipesToRemove,
    prevSlotId,
    pricing,
    isPricingPending,
    menuPending,
    menuFetchDataPending,
  ])

  const deliveryView = useMemo(() => {
    const availableDates = (deliveryDays ?? []).map((day: DeliveryDayOption) => ({
      id: day.date,
      label: day.label ?? day.date,
      isSelected: day.date === tempDate,
      isDisabled: Boolean(day.disabled),
    }))
    const selectedDate = availableDates.find((date) => date.isSelected) ?? null
    const isInstructionInvalid = !isValidDeliveryInstructionCustom
    const isContinueDisabled =
      !chosen || !hasActiveSlotsForSelectedDate || !deliveryInstruction || isInstructionInvalid

    return {
      title: 'Delivery options',
      postcodeLabel: basketPostcode,
      selectedDateId: selectedDate?.id ?? null,
      dateLabel: selectedDate?.label ?? null,
      availableDates,
      instructions: {
        value: deliveryInstruction,
        customValue: deliveryInstructionCustom,
        options: deliveryInstructionOptions,
        placeholder: deliveryInstructionsFields.placeholderText,
        customConstraints: CUSTOM_DELIVERY_INSTRUCTION_CONSTRAINTS,
        validationMessage: isInstructionInvalid ? customDeliveryInstructionValidationMessage : null,
        isRequired: true,
        isValid: isValidDeliveryInstructionCustom,
      },
      menuWeekWarning: showMenuWeekWarning ? notPrimaryMenuWeekWarningCopy : null,
      continueCta: {
        label: prevSlotId ? 'Update delivery date' : 'Continue',
        isDisabled: isContinueDisabled,
        isPending: menuPending || menuFetchDataPending,
      },
    }
  }, [
    deliveryDays,
    tempDate,
    basketPostcode,
    deliveryInstruction,
    deliveryInstructionCustom,
    isValidDeliveryInstructionCustom,
    chosen,
    hasActiveSlotsForSelectedDate,
    showMenuWeekWarning,
    prevSlotId,
    menuPending,
    menuFetchDataPending,
  ])

  return useMemo<LoggedOutBoxSummaryController>(
    () => ({
      isOpen: showDetails,
      step,
      views: {
        postcode: {
          title: 'Delivery Options',
          postcode: tempPostcode,
          constraints: POSTCODE_CONSTRAINTS,
          validationMessage: getPostcodeValidationMessage(deliveryDaysError),
          continueCta: {
            label: prevPostcode ? 'Show delivery slots' : 'Continue',
            isDisabled: trimmedPostcode.length < POSTCODE_CONSTRAINTS.minLength,
            isPending: postcodePending,
          },
        },
        delivery: deliveryView,
        portionSize: {
          title: 'Edit box size',
          selectedPortionSize,
          availablePortionSizes: availablePortionSizes.map((portionSize) => ({
            value: portionSize,
            label: portionSizesConfig[portionSize].heading,
            isSelected: portionSize === selectedPortionSize,
            details: {
              recipeRangeLabel: portionSizesConfig[portionSize].recipeInfoStrongText,
              recipeRangeSuffix: 'recipes per box.',
              suitableFor: portionSizesConfig[portionSize].suitableFor,
            },
          })),
          infoBanner:
            selectedPortionSize === 1 || selectedPortionSize === 5
              ? "You'll find measurements and instructions for this box size on your Gousto app, not on your recipe cards."
              : null,
          warningMessage: hasTooManyRecipes
            ? {
                title: `${selectedPortionSize} person boxes fit up to ${maxRecipesForPortionSize} recipes right now.`,
                recipesToRemove: numRecipesToRemove,
              }
            : null,
          confirmCta: {
            label: 'Confirm box size',
            isDisabled: !canConfirmPortionSize,
            isPending: arePortionSizesLoading,
          },
        },
        summary: summaryView,
      },
      actions,
    }),
    [
      showDetails,
      step,
      tempPostcode,
      trimmedPostcode,
      deliveryDaysError,
      prevPostcode,
      postcodePending,
      deliveryView,
      selectedPortionSize,
      availablePortionSizes,
      arePortionSizesLoading,
      canConfirmPortionSize,
      hasTooManyRecipes,
      maxRecipesForPortionSize,
      numRecipesToRemove,
      summaryView,
      actions,
    ],
  )
}
